import { COOKIECONSENTLINKS } from '@/config/cookie-consent-links.js';
import { EVENTS_SLUG_BY_LOCALE } from '@/config/events-i18n-routes.ts';
import t_SLUGFILTERCITYCENTER from '@/config/filtersTrads/t_SLUGFILTERCITYCENTER';
import t_SLUGFILTERCOUPLES from '@/config/filtersTrads/t_SLUGFILTERCOUPLES';
import t_SLUGFILTERFAMILY from '@/config/filtersTrads/t_SLUGFILTERFAMILY';
import t_SLUGFILTERKITCHEN from '@/config/filtersTrads/t_SLUGFILTERKITCHEN';
import t_SLUGFILTERPARKING from '@/config/filtersTrads/t_SLUGFILTERPARKING';
import t_SLUGFILTERPARTY from '@/config/filtersTrads/t_SLUGFILTERPARTY';
import t_SLUGFILTERPRIVATEROOMS from '@/config/filtersTrads/t_SLUGFILTERPRIVATEROOMS';
import t_SLUGFILTERSOLO from '@/config/filtersTrads/t_SLUGFILTERSOLO';
import t_SLUGPROPERTYTYPEBNB from '@/config/propertyTypesTrads/t_SLUGPROPERTYTYPEBNB';
import t_SLUGPROPERTYTYPEHOSTELS from '@/config/propertyTypesTrads/t_SLUGPROPERTYTYPEHOSTELS';
import t_SLUGPROPERTYTYPEHOTELS from '@/config/propertyTypesTrads/t_SLUGPROPERTYTYPEHOTELS';
import searchConfig from '@/config/search';
import { getSafeHttpUrl } from '@/lib/validation.js';
import { findPropertyTypeByKey } from '@/utils/propertyTypes';
import { stripUrlQueryForIndexing } from '@/utils/stripUrlQueryForIndexing.js';

export function useUrls() {
  const nuxtApp = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();
  const router = useRouter();
  const { useFormatter } = useFormatters();
  const {
    t,
    locale,
    locales,
  } = nuxtApp.$i18n;

  const testingStore = useTestingStore();
  const routeStore = useRouteStore();
  const searchStore = useSearchStore();
  const dateTime = useDateTime();

  const route = router?.currentRoute?.value;

  const envData = runtimeConfig?.public;

  /** Resolve at call time so SSR uses the request locale and SPA client navigations track i18n locale switches. */
  const getCurLocaleObj = () => useFormatter.getLocaleObjectByCode(locale?.value);

  const isRedirectDynamicSearchEnabled = testingStore.hasEnabledFeature('web_redirectDynamicSearch');
  const isRedirectDynamicPropertyEnabled = testingStore.hasEnabledFeature('web_redirectDynamicProperty');
  const isRedirectDynamicMyAccountEnabled = testingStore.hasEnabledFeature('web_redirectDynamicMyAccount');
  const isMyAccountEnabled = testingStore.hasEnabledFeature('web_IsMyAccountEnabled');

  const staticSuffix = envData.STATIC_URL;
  const dynamicSuffix = envData.DYNAMIC_URL;

  const transcodesPropertyTypes = {
    t_PROPERTYTYPEHOSTELS: t_SLUGPROPERTYTYPEHOSTELS,
    t_PROPERTYTYPEHOTELS: t_SLUGPROPERTYTYPEHOTELS,
    t_PROPERTYTYPEBNBS: t_SLUGPROPERTYTYPEBNB,
  };
  const transcodesFilters = {
    t_SLUGFILTERCITYCENTER,
    t_SLUGFILTERFAMILY,
    t_SLUGFILTERCOUPLES,
    t_SLUGFILTERKITCHEN,
    t_SLUGFILTERPARKING,
    t_SLUGFILTERPARTY,
    t_SLUGFILTERPRIVATEROOMS,
    t_SLUGFILTERSOLO,
  };

  const getPropertyTypeForHref = (propertyTypeTranscode, propertyTypeSlug, langCode) => {
    return transcodesPropertyTypes[propertyTypeTranscode]?.[langCode] || propertyTypeSlug;
  };

  const getStaticBaseURL = (langObj) => {
    const staticLocaleObj = langObj || getCurLocaleObj();
    const baseUrlKey = staticLocaleObj?.baseUrl;
    const baseUrlSegment = envData[baseUrlKey] ?? envData.BASE_URL_EN ?? '';

    // Should return something like: https://wwww.hostelworld.com/it/
    return `${envData.BASE_URL_PROTOCOL}${envData.BASE_URL_DIVIDER}${envData.DEFAULT_BASE_URL}${baseUrlSegment}${staticSuffix}`;
  };

  const getStaticBasePath = (langObj) => {
    const staticLocaleObj = langObj || getCurLocaleObj();
    const baseUrlKey = staticLocaleObj?.baseUrl;
    const baseUrlSegment = envData[baseUrlKey] ?? envData.BASE_URL_EN ?? '';

    // Should return something like: /it/
    return `${baseUrlSegment}${staticSuffix}`;
  };

  const getDynamicBaseURL = (langObj) => {
    const dynamicLocaleObj = langObj || getCurLocaleObj();

    // Should return something like: https://wwww.hostelworld.com/it/app
    return `${envData.BASE_URL_PROTOCOL}${envData.BASE_URL_DIVIDER}${envData.DEFAULT_BASE_URL}${
      envData[dynamicLocaleObj?.baseUrl]
    }${dynamicSuffix}/`;
  };

  const getPwaBaseUrl = (langObj) => {
    const pwaLocaleObj = langObj || getCurLocaleObj();
    const pwaDomain = envData?.PWA_DOMAIN_URL || envData?.DOMAIN_URL;

    // Should return something like: https://www.french.hostelworld.com/
    return `${envData.BASE_URL_PROTOCOL}${envData.BASE_URL_DIVIDER}${envData[pwaLocaleObj?.rwdUrl]}${pwaDomain}/`;
  };

  const baseStaticUrl = getStaticBaseURL();
  const baseStaticPath = getStaticBasePath();
  const baseDynamicUrl = getDynamicBaseURL();
  const basePwaUrl = getPwaBaseUrl();

  const buildExtraParams = (params) => {
    const qs = [];

    if (params && Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams(params);
      qs.push(queryParams.toString());
    }

    if (route?.query?.dev) {
      qs.push('dev=1');
    }

    return qs.length ? `?${qs.join('&')}` : '';
  };

  const buildUrlQuery = (url, queryParams) => {
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return url;
    }
    const params = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    const separator = url && url.includes('?') ? '&' : '?';
    return `${url}${separator}${params}`;
  };

  const buildPageNumbers = (pageNumber) => {
    const pageNr = Number.parseInt(pageNumber);
    return pageNr && pageNr > 1 ? `p/${pageNr}/` : '';
  };

  const buildPageFilters = (pageFilter) => {
    return pageFilter ? `f/${pageFilter}/` : '';
  };

  const getCanonicalGenericPageUrl = (urlStructure) => {
    routeStore.setRoute({ genericUrl: urlStructure });
    return `${baseStaticUrl}${urlStructure}/`;
  };

  // ################# STATIC PAGES URL GENERATION #################
  const getWorldPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/${qs}`;
  };

  const getContinentPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${qs}`;
  };

  const getInternalContinentPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticPath}${locationData?.rootSlug}/${locationData?.urlFriendlyContinent}/${qs}`;
  };

  const getCountryPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${qs}`;
  };

  const getInternalCountryPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticPath}${locationData?.rootSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${qs}`;
  };

  const getAreaPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/a/${locationData?.urlFriendlyArea}/${qs}`;
  };

  const getRegionPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/r/${locationData?.urlFriendlyRegion}/${qs}`;
  };

  const getCityPageUrl = (locationData, extraParams, pageNr) => {
    const qs = buildExtraParams(extraParams);
    const pageNumbers = buildPageNumbers(pageNr);
    const pageFilters = buildPageFilters(locationData?.urlFilterSlug);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${locationData?.urlFriendlyCity}/${pageFilters}${pageNumbers}${qs}`;
  };

  const getInternalCityPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticPath}${locationData?.rootSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${locationData?.urlFriendlyCity}/${qs}`;
  };

  const getDistrictPageUrl = (locationData, extraParams, pageNr) => {
    const qs = buildExtraParams(extraParams);
    const pageNumbers = buildPageNumbers(pageNr);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${locationData?.urlFriendlyCity}/d/${locationData?.urlFriendlyDistrict}/${pageNumbers}${qs}`;
  };

  const getPropertyPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.propertyTypeSlug}/p/${locationData.propertyId}/${locationData.urlFriendlyProperty}/${qs}`;
  };

  const get3PIPropertyPageUrl = (propertyUrl) => {
    const safeUrl = getSafeHttpUrl(propertyUrl);

    if (!safeUrl) {
      return null;
    }

    // PWA parity: test/staging/dev use the HW desktop staging white-label; production uses TPI.
    // Property ids differ per env (e.g. Cork Radisson test 99231284 vs staging/prod 16319315) —
    // keep the path id from the catalog/API and only swap the host.
    const isNonProdThreePiEnv = ['dev', 'test', 'staging'].includes(envData.RELEASE_ENV);
    const partnerHost = isNonProdThreePiEnv
      ? 'hw-desktop-staging.prod.zentrumhub.com'
      : 'www.tpi.hostelworld.com';

    try {
      const url = new URL(safeUrl);
      const isZentrumhubHost
        = url.hostname === 'zentrumhub.com' || url.hostname.endsWith('.zentrumhub.com');

      if (!isZentrumhubHost) {
        return null;
      }

      url.hostname = partnerHost;

      // Same EMPTY_DATE guard as useThreePiQueryParams — store seeds { year: 0, ... }
      // until the search form initialises, and formatToFlat would emit 1899-11-30.
      const storedCheckIn = searchStore.getSearchCheckIn;
      const storedCheckOut = searchStore.getSearchCheckOut;
      const checkIn = dateTime.isValidDate(storedCheckIn) ? storedCheckIn : dateTime.tomorrow;
      const checkOut = dateTime.isValidDate(storedCheckOut)
        ? storedCheckOut
        : dateTime.addDaysToDate(checkIn, searchConfig.defaultNights);
      url.searchParams.set('from', dateTime.formatToFlat(checkIn));
      url.searchParams.set('to', dateTime.formatToFlat(checkOut));

      return url.toString();
    } catch (error) {
      console.error('### Invalid 3PI property URL:', error);
      return null;
    }
  };

  const getPropertyPagePath = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticPath}${locationData?.propertyTypeSlug}/p/${locationData.propertyId}/${locationData.urlFriendlyProperty}/${qs}`;
  };

  const getGenericPageUrl = (urlStructure, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${urlStructure}/${qs}`;
  };

  const getCookiePolicyUrl = (extraParams = null) => {
    return getGenericPageUrl(COOKIECONSENTLINKS.COOKIESPOLICY.STATICWEB, extraParams);
  };

  const getSecurityPrivacyUrl = (extraParams = null) => {
    return getGenericPageUrl(COOKIECONSENTLINKS.SECURITYPRIVACY.STATICWEB, extraParams);
  };

  const getTermsAndConditionsUrl = (extraParams = null) => {
    return getGenericPageUrl(COOKIECONSENTLINKS.HOSTELTERMSANDCONDITIONS.STATICWEB, extraParams);
  };

  const getCouponsUrl = (extraParams = null) => {
    return getGenericPageUrl(COOKIECONSENTLINKS.COUPONS.STATICWEB, extraParams);
  };

  const getVouchersUrl = (extraParams = null) => {
    return getGenericPageUrl(COOKIECONSENTLINKS.VOUCHERS.STATICWEB, extraParams);
  };

  const getGuaranteePageUrl = (extraParams = null) => {
    return getGenericPageUrl(COOKIECONSENTLINKS.GUARANTEE.STATICWEB, extraParams);
  };

  const getReviewGuidelinesPageUrl = (extraParams = null) => {
    return getGenericPageUrl(COOKIECONSENTLINKS.REVIEWGUIDELINES.STATICWEB, extraParams);
  };

  const getReplacementForPropertyTypeFromUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    // Because English doesn't appear on the URL
    const sliceNumber = locale?.value === 'en' ? 1 : 2;
    const currentPath = route.path
      .split('/')
      .filter((param) => param)
      .slice(sliceNumber);
    const newPath = [locationData.propertyTypeSlug, ...currentPath].join('/');

    return `${baseStaticUrl}${newPath}/${qs}`;
  };

  const getPropertySignUpUrl = () => {
    let finalUrl = t('t_HOMEURLHOSTELSIGNUP');

    if (['dev', 'test'].includes(envData.RELEASE_ENV)) {
      finalUrl = t('t_HOMETESTURLHOSTELSIGNUP');
    } else if (envData.RELEASE_ENV === 'staging') {
      finalUrl = t('t_HOMESTAGINGURLHOSTELSIGNUP');
    }

    return `${finalUrl}`;
  };

  // ################# OG EVENTS PAGES URL GENERATION #################
  const getEventsHomeUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.rootSlug}/${qs}`;
  };

  const getInternalEventsHomeUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticPath}${locationData?.rootSlug}/${qs}`;
  };

  const getEventsContinentPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.rootSlug}/${locationData?.urlFriendlyContinent}/${qs}`;
  };

  const getEventsCountryPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.rootSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${qs}`;
  };

  const getEventsCityPageUrl = (locationData, extraParams, pageNr) => {
    const qs = buildExtraParams(extraParams);
    const pageNumbers = buildPageNumbers(pageNr);
    return `${baseStaticUrl}${locationData?.rootSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${locationData?.urlFriendlyCity}/${pageNumbers}${qs}`;
  };

  const getEventPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticUrl}${locationData?.rootSlug}/e/${locationData?.eventId}/${locationData?.eventNameSlug}/${qs}`;
  };

  const getInternalEventPageUrl = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${baseStaticPath}${locationData?.rootSlug}/e/${locationData?.eventId}/${locationData?.eventNameSlug}/${qs}`;
  };

  // ################# DYNAMIC PAGES URL GENERATION #################
  const getCityPageUrlDynamic = (locationData, extraParams) => {
    const dynSearchPageParams = {
      q: locationData.searchString,
      country: locationData.countryName,
      city: locationData.cityName,
      type: 'city',
      id: locationData.cityId,
    };

    if (locationData.dateCheckIn && locationData.dateCheckOut) {
      dynSearchPageParams.from = locationData.dateCheckIn;
      dynSearchPageParams.to = locationData.dateCheckOut;
    }

    if (locationData?.numOfGuests) {
      dynSearchPageParams.guests = locationData.numOfGuests;
    }

    const qs = buildExtraParams({
      ...dynSearchPageParams,
      page: 1,
      ...extraParams,
    });
    return `${isRedirectDynamicSearchEnabled ? baseDynamicUrl : basePwaUrl}pwa/s${qs}`;
  };

  // @TODO: COD-67 - Remove after A/B test
  const getRegionPageUrlDynamic = (locationData, extraParams) => {
    const humanReadableSearchQuery
      = locationData?.name && locationData?.countryName
        ? `${locationData.name}, ${locationData.countryName}`
        : `${locationData.urlFriendlyName}, ${locationData.urlFriendlyCountry}`;

    const dynSearchPageParams = {
      q: humanReadableSearchQuery,
      country: locationData.countryName,
      city: locationData.name,
      type: 'region',
      id: locationData.id,
      region: locationData.id,
      from: locationData.dateCheckIn,
      to: locationData.dateCheckOut,
      guests: locationData.numOfGuests,
      page: 1,
    };

    const qs = buildExtraParams({
      ...dynSearchPageParams,
      ...extraParams,
    });

    return `${isRedirectDynamicSearchEnabled ? baseDynamicUrl : basePwaUrl}pwa/s${qs}`;
  };

  const getAuth0CallbackPageUrl = () => {
    return `${getStaticBaseURL()}auth0-callback/`;
  };

  const getPropertyPageUrlDynamic = (locationData, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${isRedirectDynamicPropertyEnabled ? baseDynamicUrl : basePwaUrl}pwa/hosteldetails.php/${locationData?.urlFriendlyProperty}/${locationData?.urlFriendlyCity}/${locationData?.propertyId}/${qs}`;
  };

  const getMyAccountUrl = (urlStructure, extraParams) => {
    const qs = buildExtraParams(extraParams);
    return isMyAccountEnabled ? `${baseStaticPath}${urlStructure}/${qs}` : `${basePwaUrl}pwa/${urlStructure}/${qs}`;
  };

  const getLogoutPageUrlDynamic = (extraParams) => {
    const qs = buildExtraParams(extraParams);
    return `${isRedirectDynamicMyAccountEnabled ? baseDynamicUrl : basePwaUrl}pwa/logout/${qs}`;
  };

  // ################# HREF LANGS URL GENERATION #################
  const getHrefLangUrlForHomepage = (localeObj) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    return `${mainHrefLangUrl}`;
  };

  const getHrefLangUrlForWorldwide = (localeObj, locationData) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/`;
  };

  const getHrefLangUrlForContinent = (localeObj, locationData) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/`;
  };

  const getHrefLangUrlForCountry = (localeObj, locationData) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/`;
  };

  const getHrefLangUrlForArea = (localeObj, locationData) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/a/${locationData?.urlFriendlyArea}/`;
  };

  const getHrefLangUrlForRegion = (localeObj, locationData) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/r/${locationData?.urlFriendlyRegion}/`;
  };

  const getEventsRootSlugForLangCode = (langCode) => {
    return EVENTS_SLUG_BY_LOCALE[langCode] || EVENTS_SLUG_BY_LOCALE.en;
  };

  const getHrefLangUrlForEventsCountry = (localeObj, locationData, pageNr) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    const eventsRoot = getEventsRootSlugForLangCode(localeObj?.code);
    const pageNumbers = buildPageNumbers(pageNr);
    return `${mainHrefLangUrl}${eventsRoot}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${pageNumbers}`;
  };

  const getHrefLangUrlForEventsCity = (localeObj, locationData, pageNr) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    const eventsRoot = getEventsRootSlugForLangCode(localeObj?.code);
    const pageNumbers = buildPageNumbers(pageNr);
    return `${mainHrefLangUrl}${eventsRoot}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${locationData?.urlFriendlyCity}/${pageNumbers}`;
  };

  const getHrefLangUrlForEventsContinent = (localeObj, locationData) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    const eventsRoot = getEventsRootSlugForLangCode(localeObj?.code);
    return `${mainHrefLangUrl}${eventsRoot}/${locationData?.urlFriendlyContinent}/`;
  };

  const getHrefLangUrlForCity = (localeObj, locationData, pageNr) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    const pageFilters = buildPageFilters(locationData?.activeFilter ?? '');
    const pageNumbers = buildPageNumbers(pageNr);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${locationData?.urlFriendlyCity}/${pageFilters}${pageNumbers}`;
  };

  const getHrefLangUrlForDistrict = (localeObj, locationData, pageNr) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    const pageNumbers = buildPageNumbers(pageNr);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/${locationData?.urlFriendlyContinent}/${locationData?.urlFriendlyCountry}/${locationData?.urlFriendlyCity}/d/${locationData?.urlFriendlyDistrict}/${pageNumbers}`;
  };

  const getCityFilterPathSegmentForIso = (activeFilterSlugKey, isoUnder) => {
    if (!activeFilterSlugKey) {
      return '';
    }
    return transcodesFilters[activeFilterSlugKey]?.[isoUnder] || '';
  };

  const resolveCityFilterSegmentForHrefLang = (
    activeFilter,
    actualLangUnderscoreIso,
    currentLocaleFilterPathSlug,
    currentLocaleIsoUnder,
  ) => {
    if (!activeFilter) {
      return '';
    }
    if (currentLocaleFilterPathSlug && actualLangUnderscoreIso === currentLocaleIsoUnder) {
      return currentLocaleFilterPathSlug;
    }
    return getCityFilterPathSegmentForIso(activeFilter, actualLangUnderscoreIso);
  };

  const alignHrefLangAlternatesWithCanonical = (hrefLangAlternates, canonicalUrl) => {
    if (!canonicalUrl || typeof canonicalUrl !== 'string' || !Array.isArray(hrefLangAlternates)) {
      return hrefLangAlternates;
    }
    const currentLocaleCode = locale?.value;
    if (!currentLocaleCode || hrefLangAlternates.length === 0) {
      return hrefLangAlternates;
    }
    const canonicalForIndexing = stripUrlQueryForIndexing(canonicalUrl);
    const targetHid = `alternate-${currentLocaleCode}`;
    const syncXDefaultWithCanonical = getCurLocaleObj()?.isCatchallLocale === true;
    return hrefLangAlternates.map((entry) => {
      const matchesCurrentLocale = entry?.hid === targetHid;
      const matchesXDefault = syncXDefaultWithCanonical && entry?.hid === 'alternate-x-default';
      if (matchesCurrentLocale || matchesXDefault) {
        return {
          ...entry,
          href: canonicalForIndexing,
        };
      }
      return entry;
    });
  };

  const getHrefLangUrlForProperty = (localeObj, locationData) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    return `${mainHrefLangUrl}${locationData?.propertyTypeSlug}/p/${locationData?.propertyId}/${locationData?.urlFriendlyProperty}/`;
  };

  const getHrefLangUrlForGenericPage = (localeObj, urlStructure) => {
    const mainHrefLangUrl = getStaticBaseURL(localeObj);
    const actualUrlStructure = urlStructure ? `${urlStructure}/` : '';
    return `${mainHrefLangUrl}${actualUrlStructure}`;
  };

  const generateHrefLangAlternates = (pageUrlStructure) => {
    const hrefLangAlternates = [];

    locales?.value?.forEach((lang) => {
      const actualUrl = getHrefLangUrlForGenericPage(lang, pageUrlStructure);
      routeStore.setRoutesByLang(lang?.code, actualUrl);
      hrefLangAlternates.push({
        hid: `alternate-${lang?.code}`,
        rel: 'alternate',
        hreflang: lang?.hrefLang,
        href: actualUrl,
      });
      if (lang?.isCatchallLocale) {
        hrefLangAlternates.push({
          hid: 'alternate-x-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: actualUrl,
        });
      }
    });

    return hrefLangAlternates;
  };

  const generateHrefLangForWorldWide = (urlTranslations, propertyTypeKey) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        if (!actualPropertyType) {
          return;
        }
        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForWorldwide(lang, { propertyTypeSlug: propertySlugForHref });
        routeStore.setRoutesByLang(lang?.code, actualUrl);
        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForContinent = (urlTranslations, propertyTypeKey) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.worldwide?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.worldwide?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        const actualContinent = urlTranslations?.continent?.[actualLangUnderscoreIso];
        if (!actualPropertyType || !actualContinent) {
          return;
        }
        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForContinent(lang, {
          propertyTypeSlug: propertySlugForHref,
          urlFriendlyContinent: actualContinent,
        });

        routeStore.setRoutesByLang(lang?.code, actualUrl);
        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForCountry = (urlTranslations, propertyTypeKey) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.worldwide?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.worldwide?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        const actualContinent = urlTranslations?.continent?.[actualLangUnderscoreIso];
        const actualCountry = urlTranslations?.country?.[actualLangUnderscoreIso];
        if (!actualPropertyType || !actualContinent || !actualCountry) {
          return;
        }
        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForCountry(lang, {
          propertyTypeSlug: propertySlugForHref,
          urlFriendlyContinent: actualContinent,
          urlFriendlyCountry: actualCountry,
        });

        routeStore.setRoutesByLang(lang?.code, actualUrl);
        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForArea = (urlTranslations, propertyTypeKey) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.worldwide?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.worldwide?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        const actualContinent = urlTranslations?.continent?.[actualLangUnderscoreIso];
        const actualCountry = urlTranslations?.country?.[actualLangUnderscoreIso];
        const actualArea = urlTranslations?.area?.[actualLangUnderscoreIso];
        if (!actualPropertyType || !actualContinent || !actualCountry || !actualArea) {
          return;
        }

        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForArea(lang, {
          propertyTypeSlug: propertySlugForHref,
          urlFriendlyContinent: actualContinent,
          urlFriendlyCountry: actualCountry,
          urlFriendlyArea: actualArea,
        });

        routeStore.setRoutesByLang(lang?.code, actualUrl);
        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForRegion = (urlTranslations, propertyTypeKey) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.worldwide?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.worldwide?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        const actualContinent = urlTranslations?.continent?.[actualLangUnderscoreIso];
        const actualCountry = urlTranslations?.country?.[actualLangUnderscoreIso];
        const actualRegion = urlTranslations?.region?.[actualLangUnderscoreIso];
        if (!actualPropertyType || !actualContinent || !actualCountry || !actualRegion) {
          return;
        }

        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForRegion(lang, {
          propertyTypeSlug: propertySlugForHref,
          urlFriendlyContinent: actualContinent,
          urlFriendlyCountry: actualCountry,
          urlFriendlyRegion: actualRegion,
        });

        routeStore.setRoutesByLang(lang?.code, actualUrl);
        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForCity = (urlTranslations, propertyTypeKey, activeFilter, cityHrefLangOptions = {}) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const pageNr = cityHrefLangOptions?.pageNr;
    const currentLocaleFilterPathSlug = cityHrefLangOptions?.currentLocaleFilterPathSlug;
    const filterPathSegmentByIso = cityHrefLangOptions?.filterPathSegmentByIso;
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.worldwide?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.worldwide?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        const actualContinent = urlTranslations?.continent?.[actualLangUnderscoreIso];
        const actualCountry = urlTranslations?.country?.[actualLangUnderscoreIso];
        const actualCity = urlTranslations?.city?.[actualLangUnderscoreIso];
        if (!actualPropertyType || !actualContinent || !actualCountry || !actualCity) {
          return;
        }
        const actualFilter = activeFilter
          ? filterPathSegmentByIso && typeof filterPathSegmentByIso === 'object'
            ? (filterPathSegmentByIso[actualLangUnderscoreIso]
              ?? getCityFilterPathSegmentForIso(activeFilter, actualLangUnderscoreIso))
            : resolveCityFilterSegmentForHrefLang(
              activeFilter,
              actualLangUnderscoreIso,
              currentLocaleFilterPathSlug,
              currentLocaleIsoUnder,
            )
          : '';

        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForCity(
          lang,
          {
            propertyTypeSlug: propertySlugForHref,
            urlFriendlyContinent: actualContinent,
            urlFriendlyCountry: actualCountry,
            urlFriendlyCity: actualCity,
            activeFilter: actualFilter,
          },
          pageNr,
        );

        routeStore.setRoutesByLang(lang?.code, actualUrl);

        const filteredPageMissingLocaleSegment = Boolean(activeFilter) && !actualFilter;
        const isCurrentLocaleRow = actualLangUnderscoreIso === currentLocaleIsoUnder;
        if (filteredPageMissingLocaleSegment && !isCurrentLocaleRow) {
          return;
        }

        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForDistrict = (urlTranslations, propertyTypeKey, pageNr) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.worldwide?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.worldwide?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        const actualContinent = urlTranslations?.continent?.[actualLangUnderscoreIso];
        const actualCountry = urlTranslations?.country?.[actualLangUnderscoreIso];
        const actualCity = urlTranslations?.city?.[actualLangUnderscoreIso];
        const actualDistrict = urlTranslations?.district?.[actualLangUnderscoreIso];
        if (!actualPropertyType || !actualContinent || !actualCountry || !actualCity || !actualDistrict) {
          return;
        }

        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForDistrict(
          lang,
          {
            propertyTypeSlug: propertySlugForHref,
            urlFriendlyContinent: actualContinent,
            urlFriendlyCountry: actualCountry,
            urlFriendlyCity: actualCity,
            urlFriendlyDistrict: actualDistrict,
          },
          pageNr,
        );

        routeStore.setRoutesByLang(lang?.code, actualUrl);
        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForProperty = (urlTranslations, propertyTypeKey, propertyId) => {
    const hrefLangAlternates = [];
    const propertyTypeObj = findPropertyTypeByKey(propertyTypeKey);
    const currentLocaleIsoUnder = getCurLocaleObj()?.isoUnder;

    if (urlTranslations && urlTranslations?.worldwide?.[currentLocaleIsoUnder]?.[propertyTypeKey]) {
      locales?.value?.forEach((lang) => {
        const actualLangUnderscoreIso = lang?.isoUnder;
        const actualPropertyType = urlTranslations?.worldwide?.[actualLangUnderscoreIso]?.[propertyTypeKey];
        const actualProperty = urlTranslations?.property?.[actualLangUnderscoreIso];
        if (!actualPropertyType || !actualProperty) {
          return;
        }

        const propertySlugForHref = getPropertyTypeForHref(
          propertyTypeObj?.transcode,
          actualPropertyType,
          actualLangUnderscoreIso,
        );
        const actualUrl = getHrefLangUrlForProperty(lang, {
          propertyTypeSlug: propertySlugForHref,
          propertyId,
          urlFriendlyProperty: actualProperty,
        });

        routeStore.setRoutesByLang(lang?.code, actualUrl);
        hrefLangAlternates.push({
          hid: `alternate-${lang?.code}`,
          rel: 'alternate',
          hreflang: lang?.hrefLang,
          href: actualUrl,
        });
        if (lang?.isCatchallLocale) {
          hrefLangAlternates.push({
            hid: 'alternate-x-default',
            rel: 'alternate',
            hreflang: 'x-default',
            href: actualUrl,
          });
        }
      });
    }
    return hrefLangAlternates;
  };

  const generateHrefLangForEventsHome = () => {
    const hrefLangAlternates = [];

    locales?.value?.forEach((lang) => {
      const eventsRoot = getEventsRootSlugForLangCode(lang?.code);
      const actualUrl = `${getStaticBaseURL(lang)}${eventsRoot}/`;
      routeStore.setRoutesByLang(lang?.code, actualUrl);
      hrefLangAlternates.push({
        hid: `alternate-${lang?.code}`,
        rel: 'alternate',
        hreflang: lang?.hrefLang,
        href: actualUrl,
      });
      if (lang?.isCatchallLocale) {
        hrefLangAlternates.push({
          hid: 'alternate-x-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: actualUrl,
        });
      }
    });

    return hrefLangAlternates;
  };

  const generateHrefLangForEventsCountry = (urlTranslations, pageNr) => {
    const hrefLangAlternates = [];
    const currentIso = getCurLocaleObj()?.isoUnder;

    if (
      !urlTranslations
      || !currentIso
      || !urlTranslations.continent?.[currentIso]
      || !urlTranslations.country?.[currentIso]
    ) {
      return hrefLangAlternates;
    }

    locales?.value?.forEach((lang) => {
      const actualLangUnderscoreIso = lang?.isoUnder;
      const actualContinent = urlTranslations.continent?.[actualLangUnderscoreIso];
      const actualCountry = urlTranslations.country?.[actualLangUnderscoreIso];
      if (!actualContinent || !actualCountry) {
        return;
      }
      const actualUrl = getHrefLangUrlForEventsCountry(
        lang,
        {
          urlFriendlyContinent: actualContinent,
          urlFriendlyCountry: actualCountry,
        },
        pageNr,
      );

      routeStore.setRoutesByLang(lang?.code, actualUrl);
      hrefLangAlternates.push({
        hid: `alternate-${lang?.code}`,
        rel: 'alternate',
        hreflang: lang?.hrefLang,
        href: actualUrl,
      });
      if (lang?.isCatchallLocale) {
        hrefLangAlternates.push({
          hid: 'alternate-x-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: actualUrl,
        });
      }
    });
    return hrefLangAlternates;
  };

  const generateHrefLangForEventsCity = (urlTranslations, pageNr) => {
    const hrefLangAlternates = [];
    const currentIso = getCurLocaleObj()?.isoUnder;

    if (
      !urlTranslations
      || !currentIso
      || !urlTranslations.continent?.[currentIso]
      || !urlTranslations.country?.[currentIso]
      || !urlTranslations.city?.[currentIso]
    ) {
      return hrefLangAlternates;
    }

    locales?.value?.forEach((lang) => {
      const actualLangUnderscoreIso = lang?.isoUnder;
      const actualContinent = urlTranslations.continent?.[actualLangUnderscoreIso];
      const actualCountry = urlTranslations.country?.[actualLangUnderscoreIso];
      const actualCity = urlTranslations.city?.[actualLangUnderscoreIso];
      if (!actualContinent || !actualCountry || !actualCity) {
        return;
      }
      const actualUrl = getHrefLangUrlForEventsCity(
        lang,
        {
          urlFriendlyContinent: actualContinent,
          urlFriendlyCountry: actualCountry,
          urlFriendlyCity: actualCity,
        },
        pageNr,
      );

      routeStore.setRoutesByLang(lang?.code, actualUrl);
      hrefLangAlternates.push({
        hid: `alternate-${lang?.code}`,
        rel: 'alternate',
        hreflang: lang?.hrefLang,
        href: actualUrl,
      });
      if (lang?.isCatchallLocale) {
        hrefLangAlternates.push({
          hid: 'alternate-x-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: actualUrl,
        });
      }
    });
    return hrefLangAlternates;
  };

  const generateHrefLangForEventsContinent = (urlTranslations) => {
    const hrefLangAlternates = [];
    const currentIso = getCurLocaleObj()?.isoUnder;

    if (!urlTranslations || !currentIso || !urlTranslations.continent?.[currentIso]) {
      return hrefLangAlternates;
    }

    locales?.value?.forEach((lang) => {
      const actualLangUnderscoreIso = lang?.isoUnder;
      const actualContinent = urlTranslations.continent?.[actualLangUnderscoreIso];
      if (!actualContinent) {
        return;
      }
      const actualUrl = getHrefLangUrlForEventsContinent(lang, { urlFriendlyContinent: actualContinent });

      routeStore.setRoutesByLang(lang?.code, actualUrl);
      hrefLangAlternates.push({
        hid: `alternate-${lang?.code}`,
        rel: 'alternate',
        hreflang: lang?.hrefLang,
        href: actualUrl,
      });
      if (lang?.isCatchallLocale) {
        hrefLangAlternates.push({
          hid: 'alternate-x-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: actualUrl,
        });
      }
    });
    return hrefLangAlternates;
  };

  const generateHrefLangForEventsEventPage = ({
    eventId,
    eventNameSlug,
  }) => {
    const hrefLangAlternates = [];

    if (!eventId || !eventNameSlug) {
      return hrefLangAlternates;
    }

    locales?.value?.forEach((lang) => {
      const eventsRoot = getEventsRootSlugForLangCode(lang?.code);
      const actualUrl = `${getStaticBaseURL(lang)}${eventsRoot}/e/${eventId}/${eventNameSlug}/`;
      routeStore.setRoutesByLang(lang?.code, actualUrl);
      hrefLangAlternates.push({
        hid: `alternate-${lang?.code}`,
        rel: 'alternate',
        hreflang: lang?.hrefLang,
        href: actualUrl,
      });
      if (lang?.isCatchallLocale) {
        hrefLangAlternates.push({
          hid: 'alternate-x-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: actualUrl,
        });
      }
    });

    return hrefLangAlternates;
  };

  return {
    useUrl: {
      getStaticBaseURL,
      getStaticBasePath,
      getDynamicBaseURL,
      getPwaBaseUrl,
      getCanonicalGenericPageUrl,
      buildUrlQuery,

      // STATIC PAGES URL EXPORT
      getWorldPageUrl,
      getContinentPageUrl,
      getInternalContinentPageUrl,
      getCountryPageUrl,
      getInternalCountryPageUrl,
      getAreaPageUrl,
      getRegionPageUrl,
      getCityPageUrl,
      getInternalCityPageUrl,
      getDistrictPageUrl,
      getPropertyPageUrl,
      get3PIPropertyPageUrl,
      getPropertyPagePath,
      getGenericPageUrl,
      getCookiePolicyUrl,
      getSecurityPrivacyUrl,
      getTermsAndConditionsUrl,
      getGuaranteePageUrl,
      getReviewGuidelinesPageUrl,
      getCouponsUrl,
      getVouchersUrl,
      getPropertySignUpUrl,
      getReplacementForPropertyTypeFromUrl,

      // OG EVENTS PAGES URL EXPORT
      getEventsHomeUrl,
      getEventsContinentPageUrl,
      getEventsCountryPageUrl,
      getEventsCityPageUrl,
      getEventPageUrl,
      getInternalEventsHomeUrl,
      getInternalEventPageUrl,

      // DYNAMIC PAGES URL EXPORT
      getMyAccountUrl,
      getCityPageUrlDynamic,
      getRegionPageUrlDynamic,
      getPropertyPageUrlDynamic,
      getLogoutPageUrlDynamic,
      getAuth0CallbackPageUrl,

      // HREF LANGS URL EXPORT
      getHrefLangUrlForGenericPage,
      getHrefLangUrlForHomepage,
      getHrefLangUrlForContinent,
      getHrefLangUrlForCountry,
      getHrefLangUrlForArea,
      getHrefLangUrlForRegion,
      getHrefLangUrlForCity,
      getHrefLangUrlForWorldwide,
      getHrefLangUrlForProperty,
      generateHrefLangAlternates,
      generateHrefLangForWorldWide,
      generateHrefLangForContinent,
      generateHrefLangForCountry,
      generateHrefLangForCity,
      generateHrefLangForDistrict,
      generateHrefLangForArea,
      generateHrefLangForRegion,
      generateHrefLangForProperty,
      getCityFilterPathSegmentForIso,
      alignHrefLangAlternatesWithCanonical,
      generateHrefLangForEventsHome,
      generateHrefLangForEventsContinent,
      generateHrefLangForEventsCountry,
      generateHrefLangForEventsCity,
      generateHrefLangForEventsEventPage,
    },
  };
}
